import { useCallback, useEffect, useState } from "react";
import { Flag, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

export default function AdminFlaggedPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/flagged-listings");
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err, "Failed to load flagged listings"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (row, action) => {
    setBusyId(row.listing_id);
    try {
      await api.post(`/admin/flagged-listings/${row.listing_id}/${action}`);
      toast.success(action === "dismiss" ? "Reports dismissed" : "Listing unpublished");
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Action failed"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-12 md:px-10" data-testid="admin-flagged-page">
      <div className="mb-rise">
        <div className="inline-flex items-center gap-1.5 rounded-full border border-red-500/20 bg-red-50 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-red-600">
          <Flag className="h-3 w-3" />
          Moderation
        </div>
        <h1 className="mt-3 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
          Flagged listings
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-gray-500">
          Broker-reported listings awaiting moderation. Unpublishing removes
          them from matches and hides them from the owner&apos;s active inventory.
        </p>
      </div>

      <div className="mt-8 space-y-4">
        {loading && <div className="text-center text-sm text-gray-500"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></div>}
        {!loading && rows.length === 0 && (
          <div className="rounded-2xl border border-dashed border-gray-200 p-10 text-center text-sm text-gray-500" data-testid="admin-flagged-empty">
            No open reports.
          </div>
        )}
        {rows.map((r) => (
          <div
            key={r.listing_id}
            className="rounded-2xl border border-gray-100 bg-white p-5 shadow-mb-card"
            data-testid={`flagged-row-${r.listing_id}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-gray-900" data-testid={`flagged-title-${r.listing_id}`}>
                  {r.listing.title || "(untitled)"}
                </div>
                <div className="mt-1 text-xs text-gray-500">
                  {r.listing.category} · {r.listing.transaction_type} · ₱{(r.listing.price || 0).toLocaleString("en-PH")}
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Owner: <span className="font-mono">{r.broker.email}</span> · Status: <span className="uppercase">{r.listing.status}</span>
                </div>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(r.reasons || []).map((rz) => (
                    <span key={rz} className="inline-flex items-center rounded-full bg-red-50 px-2 py-0.5 text-[10px] font-semibold text-red-600">
                      {rz}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span
                  className="inline-flex items-center rounded-full bg-red-500/10 px-3 py-1 text-xs font-bold text-red-600 tabular-nums"
                  data-testid={`flagged-count-${r.listing_id}`}
                >
                  {r.count} report{r.count === 1 ? "" : "s"}
                </span>
                <div className="mt-2 flex items-center gap-2">
                  <Button
                    variant="outline" size="sm"
                    disabled={busyId === r.listing_id}
                    onClick={() => act(r, "dismiss")}
                    data-testid={`flagged-dismiss-${r.listing_id}`}
                  >
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    className="bg-red-600 text-white hover:bg-red-700"
                    disabled={busyId === r.listing_id}
                    onClick={() => act(r, "unpublish")}
                    data-testid={`flagged-unpublish-${r.listing_id}`}
                  >
                    Unpublish
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
